import { useQuery } from "@tanstack/react-query";
import { AgentReleaseInfo, getAgentReleases } from "../lib/agentReleases";
import { FALLBACK_LATEST_AGENT_VERSION } from "../lib/agentVersion";

export const agentReleasesQueryKey = ["agent", "releases"] as const;

/**
 * Shared query for the agent releases published to apt.insaion.com. The API
 * caches upstream reads, and the long stale time keeps a dashboard full of
 * version badges down to one request per session.
 */
export const useAgentReleases = () =>
  useQuery<AgentReleaseInfo>({
    queryKey: agentReleasesQueryKey,
    queryFn: getAgentReleases,
    staleTime: 30 * 60 * 1000, // 30 minutes — releases are a human-paced event
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

/**
 * The newest published agent version, e.g. "0.10.2".
 *
 * Falls back to the version baked in at build time while the query is in
 * flight, or if it fails, so the update badges never render an empty string.
 */
export const useLatestAgentVersion = (): string => {
  const { data } = useAgentReleases();
  return data?.latest_version ?? FALLBACK_LATEST_AGENT_VERSION;
};

export default useLatestAgentVersion;
